import React from 'react';

import { STEPS, STEP_LABELS, WizardStep } from './wizardState';

interface WizardStepHeaderProps {
  step: WizardStep;
  /** Big-text title for this step. Falls back to STEP_LABELS[step]. */
  title?: string;
  steps?: readonly WizardStep[];
}

/**
 * Shared step-header for the wizard. WizardChrome renders this for
 * the in-chrome steps, and EditorPage renders it at the top of the
 * editor when accessed via ?wizard=1, so step 2 (Edit) has the same
 * "title + counter + dots" affordance as every other step.
 *
 * Markup mirrors the original layout inside WizardChrome so the CSS
 * (.wizard-header / .wizard-header-row / .wizard-progress*) keeps
 * working without modification.
 */
export default function WizardStepHeader({ step, title, steps: stepsList }: WizardStepHeaderProps) {
  const activeSteps = stepsList || STEPS;
  const idx = activeSteps.indexOf(step);
  return (
    <header className="wizard-header">
      <div className="wizard-header-row">
        <h1 className="wizard-title">{title || STEP_LABELS[step]}</h1>
        <div
          className="wizard-progress"
          aria-label={`Step ${idx + 1} of ${activeSteps.length}`}
        >
          <span className="wizard-progress-counter">{idx + 1} / {activeSteps.length}</span>
          <ol className="wizard-progress-dots">
            {activeSteps.map((s, i) => (
              <li
                key={s}
                className={[
                  'wizard-progress-dot',
                  i < idx ? 'done' : '',
                  i === idx ? 'current' : '',
                ].filter(Boolean).join(' ')}
                aria-label={STEP_LABELS[s]}
                aria-current={i === idx ? 'step' : undefined}
              />
            ))}
          </ol>
        </div>
      </div>
    </header>
  );
}
